// BAO-125 Phase 3: the CLIENT half of the funnel-event registry.
//
// Deliberately a separate module from `events.ts`: that one imports
// `lib/posthog/server` (posthog-node + `next/server`), so a `'use client'`
// component importing it would drag the server SDK into the browser bundle.
// The two files share the vocabulary types and nothing else.
//
// Identity is posthog-js's own distinct id here - the browser already holds
// the anonymous id that `posthog.identify(user.id)` later merges, which is
// exactly the stitching these top-of-funnel events exist to preserve. That is
// why these events take no distinct id while the server registry demands one.
import posthog from 'posthog-js'

/**
 * Where on the marketing site a `/signup` CTA lives. The registry owns this
 * vocabulary (not the link component) so the slice dimension and the
 * component cannot drift, and so nothing has to import a React component to
 * name a CTA.
 */
export type CtaLocation =
  | 'header'
  | 'hero'
  | 'sticky_mobile'
  | 'pricing_pay'
  | 'pricing_trial'
  /** Mid-page CTA under the homepage "How it works" carousel. */
  | 'how_it_works'
  | 'footer'
  | 'our_story'
  | 'comparison'
  | 'legal'
  /** BAO-129: the homepage "Give a year" pricing card (-> /gift). */
  | 'pricing_gift'
  /** BAO-129: the "Create your account" link on the signed-out /gift/welcome page. */
  | 'gift_welcome'
  /** BAO-129: the /gift form's submit button (a button, not a link). */
  | 'gift_form'

/** Forward onboarding transitions only; a restore or a Back never emits. */
export type OnboardingStepTransition = '1_to_2' | '2_to_3'

/**
 * Where inside the signed-in app a pay/trial CTA lives.
 *
 * Deliberately a SEPARATE vocabulary from `CtaLocation`, under a separate
 * property name (`surface`, not `cta_location`). The plan's stage 7a text says
 * `cta_location`; reusing that name here would put two disjoint vocabularies
 * behind one PostHog property, so every breakdown on it would mix marketing
 * placements with in-app surfaces and neither would be readable. Recorded as
 * a Phase D amendment.
 */
export type PaySurface = 'paywall' | 'trial_ended_banner' | 'billing' | 'activate_now'

/** Event name → property schema, for events emitted from the browser. */
export interface ClientFunnelEvents {
  /** A marketing CTA that points at `/signup` was clicked. */
  marketing_cta_clicked: {
    cta_location: CtaLocation
    /** Pathname only - never the query string (it can carry tokens). */
    page: string
    /** Copy variant shown (e.g. `trial` vs `pay`); absent when unvaried. */
    cta_variant?: string
  }
  /** The onboarding wizard advanced a step. */
  onboarding_step_completed: {
    step: OnboardingStepTransition
  }
  /** The paywall rendered. One per mount, so a re-render never re-counts. */
  paywall_viewed: {
    family_id: string
    /** Whether the card-required trial was offered on this render. */
    trial_available: boolean
    /**
     * Whether the family's preview question was already generated when the
     * page rendered. A paywall shown without one reads differently: the
     * visitor saw a weaker offer.
     */
    preview_ready_at_render: boolean
  }
  /** A CTA that starts the card-required trial was clicked. */
  trial_cta_clicked: {
    family_id: string
    surface: PaySurface
    /** #332: the trial now requires a card. Kept for continuity with the pre-#332 event. */
    card_required: boolean
  }
  /** A CTA that leads to paying $99 was clicked. */
  checkout_cta_clicked: {
    family_id: string
    surface: PaySurface
  }
  /** BAO-129: the gift form was accepted and the buyer is leaving for Checkout. */
  gift_checkout_started: {
    gift_id: string
    has_message: boolean
  }
  /**
   * BAO-129: the buyer saw the confirmed thank-you page. The browser
   * person <-> gift join for the server-side `gift_purchased`.
   */
  gift_thank_you_viewed: {
    gift_id: string
  }
}

export type ClientFunnelEventName = keyof ClientFunnelEvents

/**
 * Capture a registry event from the browser. Never throws: analytics sits on
 * a click path, and posthog-js can be absent (blocked, opted out, not yet
 * loaded) - a CTA must navigate either way.
 *
 * Takes the posthog-js module singleton directly, which is this repo's
 * pattern everywhere else (`use-consent.ts`, `checkout-cta.tsx`, ...). Tests
 * reach it with `vi.mock('posthog-js')`.
 */
export function captureClientFunnelEvent<E extends ClientFunnelEventName>(
  event: E,
  props: ClientFunnelEvents[E],
): void {
  try {
    posthog.capture(event, props as Record<string, unknown>)
  } catch {
    // third-party SDK; never let it break the interaction
  }
}
